import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import LoopRoundedIcon from '@mui/icons-material/LoopRounded';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import { useDispatch, useSelector } from 'react-redux';

import WithLabel, { LABEL_STYLES } from '../../../../components/with-label/WithLabel';
import { Input } from '../../../../components/common-inputs';
import TooltipButton from '../../../../components/tooltip-button/TooltipButton';
import { BUTTON_STYLES } from '../../../../components/common-button/CommonButton';
import CommonDropdown from '../../../../components/common-dropdown/CommonDropdown';
import { IMAGES_PAGE_SIZE_OPTIONS } from '../../../../id8/components/frames/filters/FrameFilters';
import WithError from '../../../../components/with-error/WithError';
import UploadModal from '../../../../id8/components/recreate/upload-modal/UploadModal';
import AttemptsCount from './AttemptsCount';
import UploadCard from './upload-card/UploadCard';
import { changeTableSettings, setAttemptsError } from '../../../../redux/demo-recreate/demoRecreateReducer';
import { recreateAdDemo } from '../../../../redux/demo-recreate/demoRecreateActions';

import scss from './RecreateFilters.module.scss';

function RecreateFilters() {
  const { t } = useTranslation('demoAdImages');
  const dispatch = useDispatch();

  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState(null);

  const [isShowUploadModal, setIsShowUploadModal] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);

  const isImagesExist = useSelector((state) => state.demoRecreate.imagesContent.length > 0);
  const adImagesTableSettings = useSelector((state) => state.demoRecreate.imagesTableSettings);
  const attempts = useSelector((state) => state.demoRecreate.attempts);
  const isGenerating = useSelector((state) => state.demoRecreate.isGenerating);

  const handleChangeSize = (newSize) => {
    dispatch(changeTableSettings({ size: newSize }));
  };

  const handleChangeUrl = (value) => {
    setUrl(value);
    handleValidateUrl(value);
  };

  const handleValidateUrl = (value) => {
    if (!value) {
      setUrlError(t('required'));
    } else {
      setUrlError(null);
    }
  };

  const handleBlurUrl = (e) => {
    const value = e.target.value;
    handleValidateUrl(value);
  };

  const handleValidate = () => {
    if (attempts <= 0) {
      dispatch(setAttemptsError(true));
      return false;
    }

    if (!url) {
      handleValidateUrl(url);
      return false;
    }

    return true;
  };

  const handleShowUploadModal = (files) => {
    const isValid = handleValidate();
    if (!isValid) return;

    const image = files[0];
    setUploadedImage(image);
    setIsShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setIsShowUploadModal(false);
    setUploadedImage(null);
  };

  const handleRecreate = (values) => {
    dispatch(recreateAdDemo({ ...values, url }));
  };

  const isValid = useMemo(() => {
    return !!url && attempts > 0;
  }, [url, attempts]);

  return (
    <div className={scss.wrapper}>
      <div className={scss.filtersWrapper}>
        <WithLabel
          label={'URL'}
          variant={LABEL_STYLES.ROW}
          isBold
          isTwoDots
          tip={t('urlTip')}
          isRequired
          maxWidth={364}>
          <WithError isShowError={!!urlError} errorText={t(urlError)} isAbsolute>
            <Input
              value={url}
              onChange={handleChangeUrl}
              onBlur={handleBlurUrl}
              size='sm'
              name='url'
              isError={!!urlError}
            />
          </WithError>
        </WithLabel>

        <div className={scss.rightSection}>
          {isImagesExist ? (
            <div className={scss.recreateAdButton}>
              <TooltipButton
                styleColor={BUTTON_STYLES.PRIMARY}
                onClick={handleShowUploadModal}
                tooltip={isGenerating ? t('imagesRecreating') : undefined}
                isLoading={isGenerating}
                maxWidth={192}
                LeftIcon={<AddPhotoAlternateOutlinedIcon />}>
                {t('recreateAd')}
              </TooltipButton>

              <div className={scss.attemptsCountWrapper}>
                <AttemptsCount />
              </div>
            </div>
          ) : null}

          {isImagesExist ? (
            <TooltipButton
              styleColor={BUTTON_STYLES.PRIMARY}
              disabled={true}
              tooltip={t('availableInFullVersion')}
              maxWidth={192}
              LeftIcon={<LoopRoundedIcon />}>
              {t('runAgain')}
            </TooltipButton>
          ) : null}

          {isImagesExist ? (
            <TooltipButton
              styleColor={BUTTON_STYLES.PRIMARY}
              disabled={true}
              tooltip={t('availableInFullVersion')}
              maxWidth={192}
              LeftIcon={<DrawOutlinedIcon />}>
              {t('modifyConcept')}
            </TooltipButton>
          ) : null}

          <WithLabel variant={LABEL_STYLES.ROW} label={t('show')} maxWidth={168} isTwoDots>
            <CommonDropdown
              value={adImagesTableSettings.size}
              onSelect={handleChangeSize}
              options={IMAGES_PAGE_SIZE_OPTIONS}
            />
          </WithLabel>
        </div>
      </div>

      {!isImagesExist ? (
        <UploadCard onShowUploadModal={handleShowUploadModal} isValid={isValid} onValidate={handleValidate} />
      ) : null}

      <UploadModal
        isOpen={isShowUploadModal}
        onClose={handleCloseUploadModal}
        uploadedImage={uploadedImage}
        onChangeImage={setUploadedImage}
        onSubmit={handleRecreate}
      />
    </div>
  );
}

export default RecreateFilters;
